/* Shared bits of screen furniture. Layout lives with each screen; these are the pieces that
   repeat. */

.state {
  grid-column: 1 / -1;
  display: grid;
  place-items: center;
  color: var(--muted);
  font-size: var(--t-dense);
  min-height: 0;
}

.error {
  color: var(--crit);
  max-width: 48ch;
  text-align: center;
  line-height: 1.5;
}

/* A list of facts — used for method notes, limitations and the like. */
.facts {
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.fact {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.factMark {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--line-2);
  margin-top: 6px;
  flex-shrink: 0;
}

.factMarkWarn {
  background: var(--warn);
}

/* A labelled statement that a figure rests on an assumption the reader must know about. */
.caveat {
  border: 1px solid var(--warn-line);
  background: var(--warn-w);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: var(--t-min);
  color: var(--warn);
  line-height: 1.5;
}

.caveatTitle {
  font-weight: 700;
  display: block;
  margin-bottom: 2px;
}

/* One named fact inside a caveat — a company and when it joined the group, a site and when it
   closed. Its own line, because two of them run together as one sentence and a reader checking a
   group against its filings is reading a list. */
.caveatLine {
  display: block;
  margin-bottom: 3px;
}

.quality {
  display: flex;
  align-items: center;
  gap: 5px;
}

.qualityChip {
  font-size: var(--t-min);
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  color: var(--on-fill);
}

.rows {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 0;
  border-top: 1px solid var(--line);
  font-size: var(--t-min);
  width: 100%;
  background: none;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  text-align: left;
  cursor: pointer;
  color: inherit;
}

.row:first-child {
  border-top: 0;
}

.row:hover {
  background: var(--panel-2);
}

.rowName {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowSub {
  display: block;
  color: var(--muted);
  font-size: var(--t-min);
}

.rowValue {
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
}

/* Four classes that were **referenced from here and defined in `Evidence.module.css`**.
   A CSS module resolves an unknown class to `undefined`, so `className={styles.fixLink}` rendered
   nothing at all: the register's "Clear them", the record's links and Bulk edit's "Download the
   workbook" were default browser controls in the middle of a paragraph, and nothing failed — the
   markup was right and the class was absent. They live here now because these are the screens that
   ask for them. */
.note {
  margin: 0 0 12px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

/* The one action a panel is for. */
.primary {
  display: inline-block;
  padding: 6px 11px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--on-fill);
  text-decoration: none;
  background: var(--accent);
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}

.primary:hover {
  background: var(--accent-h);
}

.primary:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: not-allowed;
}

/* The third answer a reviewer can give. Outlined rather than filled, because asking for a change is
   neither the recommended act (`.primary`) nor a refusal — ADR-0053's whole point is that those are
   three different things, and two of them rendered identically would undo it in a template. */
.secondary {
  padding: 6px 11px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  cursor: pointer;
}

.secondary:hover:not(:disabled) {
  color: var(--accent);
  border-color: var(--accent);
}

.secondary:disabled {
  color: var(--ink-2);
  border-color: var(--line);
  cursor: not-allowed;
}

/* A way out of the state the reader is in — clearing a filter, retrying a failed read, opening the
   screen that would fill an empty one. Quiet, because it sits inside a sentence. */
.fixLink {
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* **A button is a target, whatever it looks like.** Unstyled it is the height of its text - 17px -
   and only the 24px circle of empty space around it (WCAG 2.5.8's spacing exception) kept it
   passing; one neighbour closer and it would not. So a button of this kind is at least
   `--target-min` tall, and `--target-touch` on a phone, where it is aimed at with a finger. A link
   (`a.fixLink`) inside a sentence is exempt as inline text and is left to the line it sits in. */
button.fixLink {
  display: inline-flex;
  align-items: center;
  min-height: var(--target-min);
}

@media (max-width: 560px) {
  button.fixLink {
    min-height: var(--target-touch);
  }
}

/* A record link inside a grid or flex card sits at its own width, not stretched across the row —
   a full-width link is a click target that reaches under the text beside it. */
.recordLink {
  place-self: start;
}

.field {
  display: grid;
  gap: 5px;
  margin-bottom: 12px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.field input,
.field select {
  padding: 7px 9px;
  font: inherit;
  font-size: 13px;
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

/* A wrapper that exists to carry a test id around a table page. The page claims the shell's whole body
   grid (`grid-column: 1 / -1`), so a box in between would squeeze it into one column: the wrapper
   takes no box of its own. */
.passThrough {
  display: contents;
}
